package org.example.project.service

import androidx.compose.runtime.mutableStateMapOf
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings

object UserSettingsApi {
    private val versions = mutableStateMapOf<String, Long>()

    suspend fun loadCompanySettingsFile(firmenId: String, userId: String, fileName: String): String {
        if (firmenId.isBlank() || userId.isBlank() || fileName.isBlank()) return ""
        return runCatching {
            decodeSettingsFileResponse(
                LumaticaApiManager.call("userverwaltung/company-settings/${apiPart(firmenId)}/${apiPart(userId)}/${apiPart(fileName)}")
            )
        }.getOrDefault("")
    }

    suspend fun saveCompanySettingsFile(firmenId: String, userId: String, fileName: String, body: String): Boolean {
        if (firmenId.isBlank() || userId.isBlank() || fileName.isBlank()) return false
        val raw = runCatching {
            LumaticaApiManager.call("userverwaltung/company-settings-save/${apiPart(firmenId)}/${apiPart(userId)}/${apiPart(fileName)}/${apiPart(body)}")
        }.getOrDefault("")
        return raw.contains("\"ok\":true")
    }

    suspend fun loadUserSettingsFile(userId: String, fileName: String): String {
        if (userId.isBlank() || fileName.isBlank()) return ""
        return runCatching {
            val firmenId = currentNetFirmenId()
            val response = if (firmenId.isBlank()) {
                LumaticaApiManager.call("userverwaltung/settings/${apiPart(userId)}/${apiPart(fileName)}")
            } else {
                LumaticaApiManager.call("userverwaltung/company-settings/${apiPart(firmenId)}/${apiPart(userId)}/${apiPart(fileName)}")
            }
            decodeSettingsFileResponse(response)
        }.getOrDefault("")
    }

    suspend fun saveUserSettingsFile(userId: String, fileName: String, body: String): Boolean {
        if (userId.isBlank() || fileName.isBlank()) return false
        val raw = runCatching {
            val firmenId = currentNetFirmenId()
            if (firmenId.isBlank()) {
                LumaticaApiManager.call("userverwaltung/settings-save/${apiPart(userId)}/${apiPart(fileName)}/${apiPart(body)}")
            } else {
                LumaticaApiManager.call("userverwaltung/company-settings-save/${apiPart(firmenId)}/${apiPart(userId)}/${apiPart(fileName)}/${apiPart(body)}")
            }
        }.getOrDefault("")
        return raw.contains("\"ok\":true")
    }

    suspend fun loadUserSmartRingSettings(userId: String): String {
        if (userId.isBlank()) return ""
        return runCatching {
            val firmenId = currentNetFirmenId()
            if (firmenId.isBlank()) {
                LumaticaApiManager.call("userverwaltung/smartring/${apiPart(userId)}")
            } else {
                LumaticaApiManager.call("userverwaltung/company-smartring/${apiPart(firmenId)}/${apiPart(userId)}")
            }
        }.getOrDefault("")
    }

    suspend fun saveUserSmartRingSettings(userId: String, body: String): Boolean {
        if (userId.isBlank()) return false
        val raw = runCatching {
            val firmenId = currentNetFirmenId()
            if (firmenId.isBlank()) {
                LumaticaApiManager.call("userverwaltung/smartring-save/${apiPart(userId)}/${apiPart(body)}")
            } else {
                LumaticaApiManager.call("userverwaltung/company-smartring-save/${apiPart(firmenId)}/${apiPart(userId)}/${apiPart(body)}")
            }
        }.getOrDefault("")
        return raw.contains("\"ok\":true")
    }

    suspend fun loadCurrentUserSettingsFile(fileName: String): String =
        loadUserSettingsFile(LoginService.getId(), fileName)

    suspend fun saveCurrentUserSettingsFile(fileName: String, body: String): Boolean =
        saveUserSettingsFile(LoginService.getId(), fileName, body)

    suspend fun loadCurrentUserRegalSettingsFile(): String {
        val userId = LoginService.getId()
        if (userId.isBlank()) return ""
        return runCatching {
            LumaticaApiManager.call("userverwaltung/regalsettings/${apiPart(userId)}")
        }.getOrDefault("")
    }

    suspend fun saveCurrentUserRegalSettingsFile(body: String): Boolean {
        val userId = LoginService.getId()
        if (userId.isBlank()) return false
        val raw = runCatching {
            LumaticaApiManager.call("userverwaltung/regalsettings-save/${apiPart(userId)}/${apiPart(body)}")
        }.getOrDefault("")
        return raw.contains("\"ok\":true")
    }

    fun version(fileName: String): Long = versions[fileName] ?: 0L

    fun markChanged(fileName: String) {
        if (fileName.isBlank()) return
        versions[fileName] = (versions[fileName] ?: 0L) + 1L
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

    private fun decodeSettingsFileResponse(response: String): String =
        runCatching { Json.decodeFromString<String>(response) }.getOrDefault(response)

    private fun currentNetFirmenId(): String {
        val isNet = runCatching { LumaticaApiSettings.nodePort.trim() == "2000" }.getOrDefault(false)
        return if (isNet) LoginService.getUser()?.firmenId.orEmpty().trim() else ""
    }
}
